import { useCallback, useEffect, useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { Monitor, Shield } from 'lucide-react';
import { Badge, Button, Card, ConfirmDialog, Spinner, useToast } from '@centraoneplus/ui';
import { authService, type ActiveSession } from '@services/auth.service';

/**
 * The signed-in user's sessions, with sign-out per session and for all
 * others (VAPT 2026-10-16, finding 8). Replaces a hard-coded placeholder.
 */

/** "Chrome on Windows" from a user-agent string; good enough to recognise a device. */
export function describeDevice(userAgent: string | null): string {
  if (!userAgent) return 'Unknown device';
  const browser = /Edg\//.test(userAgent)
    ? 'Edge'
    : /OPR\/|Opera/.test(userAgent)
      ? 'Opera'
      : /Firefox\//.test(userAgent)
        ? 'Firefox'
        : /Chrome\//.test(userAgent)
          ? 'Chrome'
          : /Safari\//.test(userAgent)
            ? 'Safari'
            : 'Browser';
  const os = /Windows/.test(userAgent)
    ? 'Windows'
    : /Android/.test(userAgent)
      ? 'Android'
      : /iPhone|iPad|iOS/.test(userAgent)
        ? 'iOS'
        : /Mac OS X|Macintosh/.test(userAgent)
          ? 'macOS'
          : /Linux/.test(userAgent)
            ? 'Linux'
            : 'unknown OS';
  return `${browser} on ${os}`;
}

function ago(iso: string | null): string {
  if (!iso) return 'unknown';
  return formatDistanceToNow(new Date(iso), { addSuffix: true });
}

export function ActiveSessionsCard() {
  const [sessions, setSessions] = useState<ActiveSession[] | null>(null);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);
  const [confirmOthers, setConfirmOthers] = useState(false);
  const [revokingOthers, setRevokingOthers] = useState(false);
  const toast = useToast();

  const load = useCallback(async () => {
    try {
      setError('');
      setSessions(await authService.listSessions());
    } catch {
      setError('Could not load your sessions. Try again later.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const signOut = async (id: string) => {
    setBusyId(id);
    try {
      await authService.revokeSession(id);
      toast.success('Session signed out');
      await load();
    } catch {
      toast.error('Could not sign out that session');
    } finally {
      setBusyId(null);
    }
  };

  const signOutOthers = async () => {
    setRevokingOthers(true);
    try {
      const { revoked } = await authService.revokeOtherSessions();
      toast.success(revoked === 1 ? '1 other session signed out' : `${revoked} other sessions signed out`);
      setConfirmOthers(false);
      await load();
    } catch {
      toast.error('Could not sign out your other sessions');
    } finally {
      setRevokingOthers(false);
    }
  };

  const others = sessions?.filter((s) => !s.current) ?? [];

  return (
    <Card className="p-6">
      <div className="flex items-start gap-4">
        <div className="h-10 w-10 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
          <Shield className="h-5 w-5 text-gray-600 dark:text-gray-400" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h3 className="text-sm font-medium text-gray-900 dark:text-white">Active Sessions</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Browsers signed in to your account. Sign out any you don&apos;t recognise, then change your password.
              </p>
            </div>
            {others.length > 0 && (
              <Button variant="outline" size="sm" onClick={() => setConfirmOthers(true)}>
                Sign out other sessions
              </Button>
            )}
          </div>

          <div className="mt-4 text-sm">
            {error && <p className="text-red-600 dark:text-red-400">{error}</p>}
            {!error && sessions === null && <Spinner size="sm" />}
            {sessions?.map((s) => (
              <div
                key={s.id}
                className="flex items-center justify-between gap-3 py-3 border-b last:border-b-0 border-gray-100 dark:border-gray-700"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <Monitor className="h-4 w-4 shrink-0 text-gray-400" />
                  <div className="min-w-0">
                    <p className="font-medium text-gray-900 dark:text-white truncate">{describeDevice(s.userAgent)}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {s.ipAddress ?? 'Unknown IP'} · signed in {ago(s.startedAt)} · last active {ago(s.lastActiveAt)}
                    </p>
                  </div>
                </div>
                {s.current ? (
                  <Badge variant="success">This browser</Badge>
                ) : (
                  <Button variant="outline" size="sm" disabled={busyId === s.id} onClick={() => signOut(s.id)}>
                    {busyId === s.id ? 'Signing out...' : 'Sign out'}
                  </Button>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      <ConfirmDialog
        isOpen={confirmOthers}
        onClose={() => setConfirmOthers(false)}
        onConfirm={signOutOthers}
        loading={revokingOthers}
        variant="danger"
        title="Sign out other sessions?"
        message="Every other browser signed in to your account will be signed out. This browser stays signed in."
        confirmLabel="Sign out others"
        cancelLabel="Cancel"
      />
    </Card>
  );
}
